import React from 'react';
import CustomerInlineLabelInput from 'components/Customer/CustomerInlineLabelInput';
import { PropTypes } from 'prop-types';
import { Field, FormSection } from 'redux-form';
import { DidYouMeanCombo } from 'components/Common/DidYouMeans/DidYouMeanCombo';

const CustomerAccountPanel = ({ accounts, isEditingCustomer, jurisdictions, onAccountChange }) => {
  const ftinRequirements = [
    // The bound value relates to flag "FtinNotRequired". Bool values inverted.
    { value: false, description: 'Required' },
    { value: true, description: 'Not Required' }
  ];

  const parseDescriptionToValue = (description, keyValuePairs) => {
    const requirement = keyValuePairs?.find(req => req.description === description);
    return requirement?.value ?? '';
  };

  const formatValueAsDescription = (value, keyValuePairs) => {
    const requirement = keyValuePairs?.find(req => req.value === value);
    return requirement?.description ?? '';
  };

  const USTinType = [
    { value: 1, description: 'SSN' },
    { value: 2, description: 'EIN' }
  ];

  const getAccountName = (account) => {
    if (account === null || (!account.accountNumber && !account.accountName)) {
      return 'N/A';
    }

    let accountName = account.accountNumber;

    if (!accountName) {
      accountName = account.accountName;
    } else if (account.accountName !== null && account.accountName !== accountName) {
      accountName = `${ accountName } - ${ account.accountName }`;
    }

    accountName = `${ accountName } ${ account.isActive ? '' : '(Inactive)' }`;

    return accountName;
  };

  return (
    <div className="customer-account-panel">
      <div className="customer-account-panel-header">
        <h2 id="customer-account-cert-header">
          <strong>Account Certification:</strong>
        </h2>
        <span className="dropdown-account-number-label" id="dropdown-customer-edit-account-label">Account:</span>
        <div className="dropdown-account-number-wrapper" id="dropdown-customer-edit-account-number">
          <select
            id="dropdown-account-number"
            className="dropdown-account-number form-select"
            onChange={ e => onAccountChange(e.target.value) }
            disabled={ isEditingCustomer }
          >
            {accounts && accounts.map((item, index) => (
              <option
                key={ item.account.id }
                id="dropdown-customer-edit-account-number-option"
                value={ index }
              >
                {getAccountName(item.account)}
              </option>)) }
          </select>
        </div>
      </div>
      <div className="customer-form-panel">
        <div className="panel">
          <div className="panel-data">
            <FormSection name="certificationData">
              <div className="columns">
                <div className="col-6">
                  <Field
                    name="tin"
                    component={ CustomerInlineLabelInput }
                    isEditable={ isEditingCustomer }
                    label="U.S. TIN"
                    id="customer-account-us-tin"
                  />
                  <div className="form-group columns">
                    <label className="form-label col-3" id="customer-account-tin-type-label">TIN Type</label>
                    <span className="col-8">
                      <Field
                        className="form-select"
                        name="tinType"
                        component={ DidYouMeanCombo }
                        items={ USTinType.map(x => x.description) }
                        parse={ value => parseDescriptionToValue(value, USTinType) }
                        format={ value => formatValueAsDescription(value, USTinType) }
                        disabled={ !isEditingCustomer }
                        id="customer-account-tin-type"
                      />
                    </span>
                  </div>
                  <Field
                    name="giin"
                    component={ CustomerInlineLabelInput }
                    isEditable={ isEditingCustomer }
                    label="GIIN"
                    id="customer-account-giin"
                  />
                  <Field
                    name="part2Giin"
                    component={ CustomerInlineLabelInput }
                    isEditable={ isEditingCustomer }
                    label="Part 2 GIIN"
                    id="customer-account-part2-giin"
                  />
                </div>
                <div className="col-6">
                  <div className="form-group columns">
                    <label className="form-label col-3" id="customer-account-treaty-country-label">Treaty Country</label>
                    <span className="col-8">
                      <Field
                        name="treatyCountry"
                        component={ DidYouMeanCombo }
                        items={ jurisdictions?.map(x => x.name) ?? [] }
                        disabled={ !isEditingCustomer }
                        className="form-select"
                        id="customer-account-treaty-country"
                      />
                    </span>
                  </div>
                  <Field
                    name="foreignTin"
                    component={ CustomerInlineLabelInput }
                    isEditable={ isEditingCustomer }
                    label="Foreign TIN"
                    id="customer-account-ftin"
                  />
                  <div className="form-group columns">
                    <label className="form-label col-3" id="customer-account-ftin-required-label">FTIN Required</label>
                    <span className="col-8">
                      <Field
                        className="form-select"
                        name="foreignTinNotRequired"
                        component={ DidYouMeanCombo }
                        items={ ftinRequirements.map(x => x.description) }
                        parse={ value => parseDescriptionToValue(value, ftinRequirements) }
                        format={ value => formatValueAsDescription(value, ftinRequirements) }
                        disabled={ !isEditingCustomer }
                        id="customer-account-ftin-required"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </FormSection>
          </div>
        </div>
      </div>
    </div>
  );
};

CustomerAccountPanel.propTypes = {
  accounts: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isEditingCustomer: PropTypes.bool,
  jurisdictions: PropTypes.arrayOf(PropTypes.shape()),
  onAccountChange: PropTypes.func
};

CustomerAccountPanel.defaultProps = {
  jurisdictions: null,
  isEditingCustomer: false,
  onAccountChange: null
};

export default CustomerAccountPanel;
